import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Service, TeamMember, Work } from '../types/api';
import { Button, Carousel, CtaLink, Eyebrow, ProjectCard, Reveal, RichText, RichHtml, SafeImage, ScrollJack, Section } from '../components/ui';
import { ServiceHero } from '../components/service-page';
import { SectorsGrid, TeamSpotlight } from '../components/home';
import { useNavData } from '../components/layout/useNavData';
import { capitalize, formatLocation, paragraphs } from '../utils/text';
import { parseWorkText } from '../utils/work';

const bad = (u?: string) => !u || /placeholder\.com/i.test(u);
const when = (w: Work) => (w.completed_date ? Date.parse(w.completed_date) : 0) || 0;
/** En dessous de cette longueur, la description tient dans le hero; au-delà, elle a ses propres blocs. */
const SHORT = 300;
/** Une phrase d'accroche plus longue que ça ne tient pas en grand titre (PCL: environ 4 lignes). */
const HEADLINE_MAX = 110;

export default function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const [service, setService] = useState<Service | null>(null);
  const [works, setWorks] = useState<Work[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const { sectors } = useNavData();

  useEffect(() => {
    if (!id) { setStatus('error'); return; }
    let alive = true;
    setStatus('loading'); setWorks([]);
    apiService.getService(id)
      .then((s) => {
        if (!alive) return;
        if (!s.enable) { setStatus('error'); return; }
        setService(s); setStatus('ok');
        apiService.getWorksByService(id).then((w) => alive && setWorks(w.filter((x) => x.cover_image?.url).sort((a, b) => when(b) - when(a)))).catch(() => {});
        apiService.getTeamMembers().then((t) => alive && setTeam(t)).catch(() => {});
      })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, [id]);

  const desc = service?.description?.trim() ?? '';
  const overview = service?.over_view_service;
  const long = desc.length > SHORT;

  // Hero: la description si elle est courte, sinon le résumé « vue d'ensemble » de la base
  const lead = long ? (overview?.description?.trim() || paragraphs(desc)[0]) : desc;

  // Bloc « grand titre à gauche, texte à droite »: la première phrase en titre si elle est assez courte
  const { headline, body } = useMemo(() => {
    if (!long) return { headline: '', body: [] };
    // Texte riche saisi dans l'admin: on l'affiche tel quel (assaini) sous un titre fixe
    if (service?.descriptionHtml) return { headline: 'En détail', body: [{ kind: 'p' as const, text: desc }] };
    const first = paragraphs(desc)[0] ?? '';
    const firstSentence = first.split(/(?<=[.!?])\s+/)[0];
    if (firstSentence.length <= HEADLINE_MAX) {
      const rest = [first.slice(firstSentence.length).trim(), ...paragraphs(desc).slice(1)].filter(Boolean).join('\n');
      return { headline: firstSentence, body: parseWorkText(rest).blocks };
    }
    return { headline: 'En détail', body: parseWorkText(desc).blocks };
  }, [long, desc, service?.descriptionHtml]);

  const bandImage = long && overview?.image?.url && !bad(overview.image.url) && overview.image.url !== service?.cover_image?.url ? overview.image.url : undefined;
  const approaches = (service?.approaches ?? []).filter((a) => a.enable !== false);
  const jackItems = approaches.filter((a) => !bad(a.image?.url)).map((a) => ({ id: a.id, title: a.title, text: a.description, image: a.image?.url }));

  const sector = sectors.find((s) => s.id === service?.sector_id);
  const related = useMemo(() => {
    const ok = sectors.filter((s) => s.id !== service?.sector_id);
    return [...ok.filter((s) => s.domain_id === sector?.domain_id), ...ok.filter((s) => s.domain_id !== sector?.domain_id)];
  }, [sectors, sector, service?.sector_id]);

  if (status === 'loading') {
    return <div aria-busy className="animate-pulse"><div className="grid lg:grid-cols-2"><div className="space-y-4 p-12"><div className="h-4 w-40 bg-secondary-200" /><div className="h-16 w-3/4 bg-secondary-200" /></div><div className="h-80 bg-secondary-200 lg:h-[38rem]" /></div></div>;
  }
  if (status === 'error' || !service) {
    return (
      <Section eyebrow="Services" heading="Service introuvable" background="white">
        <p className="mb-8 max-w-xl text-lg text-ink">Ce service n'existe pas ou n'est plus disponible.</p>
        <CtaLink to="/services">Voir tous les services</CtaLink>
      </Section>
    );
  }

  return (
    <div id="content" className="text-black">
      {/* 1. Hero */}
      <ServiceHero eyebrow={service.sector?.name ? `Service · ${service.sector.name}` : 'Service'} title={service.name} image={service.cover_image?.url}>
        <p className="text-[1.0625rem] leading-relaxed !text-black">{lead}</p>
        <div className="mt-8"><Button to={`/contact?service=${service.id}`}>Démarrer un projet</Button></div>
      </ServiceHero>

      {/* 2. Grand titre à gauche, texte à droite */}
      {body.length > 0 && (
        <section className="bg-white py-16 md:py-28">
          <div className="site-container grid gap-10 lg:grid-cols-2 lg:gap-24">
            <Reveal><h2 className="font-display text-[1.75rem] font-bold normal-case leading-[1.12] tracking-normal text-black md:text-[2.5rem]">{headline}</h2></Reveal>
            <Reveal delay={0.08}>{service?.descriptionHtml ? <RichHtml html={service.descriptionHtml} /> : <RichText blocks={body} />}</Reveal>
          </div>
        </section>
      )}

      {/* 3. Grande photo pleine largeur */}
      {bandImage && <Reveal><div className="h-[18rem] overflow-hidden bg-secondary-200 md:h-[34rem]"><img src={bandImage} alt={service.name} loading="lazy" className="h-full w-full object-cover" /></div></Reveal>}

      {/* 4. Réussir ensemble */}
      <section className="bg-white py-16 md:py-28">
        <div className="site-container grid gap-10 lg:grid-cols-2 lg:gap-24">
          <Reveal><h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">Réussir ensemble</h2></Reveal>
          <Reveal delay={0.08}>
            <p className="text-[1.0625rem] leading-relaxed">De l'étude à la remise des clés, notre équipe travaille à vos côtés pour tenir le budget, les délais et la qualité de chaque ouvrage.</p>
            <div className="mt-6"><CtaLink to="/contact">Nous contacter</CtaLink></div>
          </Reveal>
        </div>
      </section>

      {/* 5. Approches: défilement piloté (une seule approche: simple bloc photo + texte) */}
      {jackItems.length > 1 && <ScrollJack eyebrow={`La valeur de ${service.name.toLowerCase()}`} items={jackItems} />}
      {jackItems.length === 1 && (
        <section className="bg-white py-16 md:py-24">
          <div className="site-container grid items-center gap-10 lg:grid-cols-2 lg:gap-20">
            <SafeImage src={jackItems[0].image} alt={jackItems[0].title} className="aspect-[4/3] w-full object-cover" />
            <div><Eyebrow className="mb-4">Notre approche</Eyebrow><h3 className="font-sans text-[2rem] font-bold normal-case leading-tight tracking-normal text-black md:text-[2.25rem]">{jackItems[0].title}</h3><p className="mt-4 text-[1.0625rem] leading-relaxed">{jackItems[0].text}</p></div>
          </div>
        </section>
      )}

      {/* 6. Projets réalisés */}
      {works.length > 0 && (
        <section className="bg-white py-16 md:py-24">
          <div className="site-container">
            <Carousel variant="projects" eyebrow="Projets réalisés" title="Nos réalisations dans ce service">
              {works.map((w) => <ProjectCard key={w.id} to={`/works/${w.id}`} image={w.cover_image?.url} title={capitalize(w.title)} location={formatLocation(w.location)} />)}
            </Carousel>
          </div>
        </section>
      )}

      {/* 7. Équipe */}
      {team.length > 0 && <TeamSpotlight members={team} compact />}

      {/* 8. Secteurs associés */}
      <SectorsGrid sectors={related} eyebrow="Secteurs associés" heading="Là où ce service intervient" background="white" />
    </div>
  );
}
